import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionProvider } from './lib/session';
import { TasksProvider } from './lib/tasks';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth failures are terminal; retrying just delays the redirect to login.
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root element missing from index.html');

createRoot(root).render(
  <StrictMode>
    {/*
      The last resort, above every provider. The shell has its own boundary
      around the page (`AppShell`), which is the one that normally answers;
      this one exists for a throw in the shell, a provider or the login page,
      where the alternative is React unmounting `#root` to a white window —
      what the bulk page's update loop did on 2026-09-13. Plain-styled on the
      tokens alone, because nothing inside the tree can be trusted here.
    */}
    <ErrorBoundary variant="plain">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            {/*
              Inside the router and the query client, outside every page: work a
              dialog starts has to outlive the dialog, and its toast has to find
              the operator wherever they navigated to.
            */}
            <TasksProvider>
              <App />
            </TasksProvider>
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
